/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  AddCompetenceLevelRequest,
  CompetenceLevel,
  CompetenceModel,
  CompetencyV3,
  CreateCompetenceRequest,
  SaveCompetencyRequest,
} from '../../models/competence/competence.model';
import { ObservableResult } from '../../store';
import { RestClientService } from '../rest-client.service';
import { CompetenceDataService } from './competence-data.service';

@Injectable()
export class ApiCompetenceDataService implements CompetenceDataService {
  private competenciesUrl = Location.joinWithSlash(environment.apiRootUrl || '', 'competencies');

  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  createCompetence(request: CreateCompetenceRequest): ObservableResult<CompetenceModel> {
    return this.client.post<CompetenceModel>(this.competenciesUrl, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => ObservableResult.ofError(JSON.stringify(err))),
    );
  }

  updateCompetence(competenceUid: string, request: CreateCompetenceRequest): ObservableResult<CompetenceModel> {
    return this.client.patch<CompetenceModel>(this.competenciesUrl + `/${competenceUid}`, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError((err) => ObservableResult.ofError(JSON.stringify(err))),
    );
  }

  addCompetenceLevel(competenceUid: string, request: AddCompetenceLevelRequest): ObservableResult<CompetenceModel> {
    return this.client.post<CompetenceModel>(this.competenciesUrl + `/${competenceUid}/levels`, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAddCompetenceLevel'))),
    );
  }

  updateCompetenceLevel(
    competenceUid: string,
    competenceLevelUid: string,
    request: AddCompetenceLevelRequest,
  ): ObservableResult<CompetenceLevel> {
    return this.client.patch<CompetenceLevel>(this.competenciesUrl + `/${competenceUid}/levels/${competenceLevelUid}`, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorUpdateCompetenceLevel'))),
    );
  }

  getCompetenceById(competenceUid: string): ObservableResult<CompetenceModel> {
    return this.client.get<CompetenceModel>(this.competenciesUrl + `/${competenceUid}`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetCompetence'))),
    );
  }

  getOrganizationCompetencies(): ObservableResult<CompetenceModel[]> {
    return this.client.get<CompetenceModel[]>(this.competenciesUrl).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetCompetenceList'))),
    );
  }

  connectCompetenceToTag(tagUid: string, competencyUid: string): ObservableResult<void> {
    return this.client.post<void>(Location.joinWithSlash(environment.apiRootUrl || '', `tags/${tagUid}/competency/${competencyUid}`)).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorAssignCompetence'))),
    );
  }

  getGlobalCompetencies(): ObservableResult<CompetencyV3[]> {
    return this.client.get<CompetencyV3[]>(this.competenciesUrl + '/global').pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body ?? [])),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorGetCompetenceList'))),
    );
  }

  saveCompetency(request: SaveCompetencyRequest): ObservableResult<CompetencyV3> {
    return this.client.post<CompetencyV3>(this.competenciesUrl, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorSaveCompetence'))),
    );
  }

  updateCompetencyFull(competencyUid: string, request: SaveCompetencyRequest): ObservableResult<CompetencyV3> {
    return this.client.put<CompetencyV3>(this.competenciesUrl + `/${competencyUid}`, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService?.translate('translations.errors.errorSaveCompetence'))),
    );
  }
}
